import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { useRef, useState, type ChangeEvent } from 'react'
import {
  useClubAdminAddMemberBatchMutation,
  useClubAdminImportPreviewMutation,
} from '../../features/SpeechAgendaApiSlice.ts'
import LoadingSpinner from '../icons/LoadingSpinner.tsx'
import type {
  ImportPreviewRow,
  ImportRowStatus,
} from '../../types/Types.ts'
import type {
  BatchAddMemberResult,
  BatchAddMembersResponse,
} from '../../generated'
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query'
import type { SerializedError } from '@reduxjs/toolkit'

export interface ImportMembersDialogProps {
  isOpen: boolean
  onClose: () => void
  clubId: string
}

const STATUS_BADGES: Record<
  ImportRowStatus,
  { label: string; className: string }
> = {
  IMPORTABLE: {
    label: 'Importable',
    className: 'bg-green-50 text-green-700 ring-green-600/20',
  },
  MISSING_NAME: {
    label: 'Missing name',
    className: 'bg-red-50 text-red-700 ring-red-600/20',
  },
  MISSING_EMAIL: {
    label: 'Missing email',
    className: 'bg-red-50 text-red-700 ring-red-600/20',
  },
  INVALID_EMAIL: {
    label: 'Invalid email',
    className: 'bg-red-50 text-red-700 ring-red-600/20',
  },
  DUPLICATE_IN_FILE: {
    label: 'Duplicate in file',
    className: 'bg-amber-50 text-amber-700 ring-amber-600/20',
  },
  ALREADY_MEMBER: {
    label: 'Already a member',
    className: 'bg-amber-50 text-amber-700 ring-amber-600/20',
  },
}

// New members are imported with the default "Member" club role.
const MEMBER_ROLE_ID = 2

const StatusBadge = ({ status }: { status: ImportRowStatus }) => {
  const badge = STATUS_BADGES[status]
  return (
    <span
      className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${badge.className}`}>
      {badge.label}
    </span>
  )
}

const resolveImportErrorMessage = (error: unknown): string => {
  if (!error || typeof error !== 'object') {
    return 'Unable to read the file. Try again later'
  }

  if ('data' in error) {
    const data = (error as FetchBaseQueryError).data
    if (typeof data === 'string' && data.trim().length > 0) {
      return data
    }
    if (data && typeof data === 'object' && 'message' in data) {
      const message = (data as { message?: unknown }).message
      if (typeof message === 'string' && message.trim().length > 0) {
        return message
      }
    }
  }

  if (
    'message' in (error as SerializedError) &&
    (error as SerializedError).message
  ) {
    return String((error as SerializedError).message)
  }

  return 'Unable to read the file. Try again later'
}

interface SummaryStat {
  label: string
  value: number
  className: string
}

const SummaryBar = ({ stats }: { stats: SummaryStat[] }) => (
  <dl className="grid grid-cols-2 gap-3 sm:grid-cols-5">
    {stats.map((stat) => (
      <div
        key={stat.label}
        className={`rounded-md px-3 py-2 text-center ${stat.className}`}>
        <dt className="text-xs font-medium">{stat.label}</dt>
        <dd className="mt-0.5 text-lg font-semibold">{stat.value}</dd>
      </div>
    ))}
  </dl>
)

const RESULT_BADGES: Record<
  BatchAddMemberResult['status'],
  { label: string; className: string }
> = {
  CREATED: {
    label: 'Created',
    className: 'bg-green-50 text-green-700 ring-green-600/20',
  },
  SKIPPED_ALREADY_MEMBER: {
    label: 'Skipped',
    className: 'bg-amber-50 text-amber-700 ring-amber-600/20',
  },
  FAILED: {
    label: 'Failed',
    className: 'bg-red-50 text-red-700 ring-red-600/20',
  },
}

const ImportResultSummary = ({
  result,
}: {
  result: BatchAddMembersResponse
}) => {
  const { summary, results } = result
  // Anything that isn't a clean create is worth surfacing to the officer.
  const flagged = results.filter((row) => row.status !== 'CREATED')

  return (
    <div className="mt-6 rounded-md bg-green-50 px-4 py-4">
      <p className="text-sm font-semibold text-green-800">Import complete</p>
      <p className="mt-1 text-sm text-green-700">
        Created {summary.created}, skipped {summary.skipped}, failed{' '}
        {summary.failed}
      </p>

      {flagged.length > 0 && (
        <ul className="mt-3 space-y-2 border-t border-green-200 pt-3">
          {flagged.map((row, index) => {
            const badge = RESULT_BADGES[row.status]
            return (
              <li
                key={`${row.email}-${index}`}
                className="flex items-start justify-between gap-3 text-sm">
                <span className="min-w-0">
                  <span className="font-medium text-gray-900">{row.name}</span>{' '}
                  <span className="text-gray-500">{row.email}</span>
                  {row.message && (
                    <span className="block text-xs text-gray-400">
                      {row.message}
                    </span>
                  )}
                </span>
                <span
                  className={`inline-flex shrink-0 items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${badge.className}`}>
                  {badge.label}
                </span>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

const ImportMembersDialog = ({
  isOpen,
  onClose,
  clubId,
}: ImportMembersDialogProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [fileName, setFileName] = useState<string | null>(null)
  // Parsed rows are kept in state so the commit step (Task 4) can read the
  // IMPORTABLE ones.
  const [rows, setRows] = useState<ImportPreviewRow[]>([])
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const [importPreview, { data: preview, isLoading, reset }] =
    useClubAdminImportPreviewMutation()
  const [
    addMemberBatch,
    { data: batchResult, isLoading: isImporting, reset: resetBatch },
  ] = useClubAdminAddMemberBatchMutation()

  const summary = preview?.summary
  const importableRows = rows.filter((row) => row.status === 'IMPORTABLE')

  const resetState = () => {
    setFileName(null)
    setRows([])
    setErrorMessage(null)
    reset()
    resetBatch()
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleClose = () => {
    resetState()
    onClose()
  }

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) {
      return
    }

    setFileName(file.name)
    setErrorMessage(null)
    setRows([])
    resetBatch()

    try {
      const response = await importPreview({ clubId, file }).unwrap()
      setRows(response.rows)
    } catch (error) {
      console.error('Failed to preview member import', error)
      setErrorMessage(resolveImportErrorMessage(error))
    }
  }

  const handleImport = async () => {
    if (importableRows.length === 0) {
      return
    }

    setErrorMessage(null)

    try {
      await addMemberBatch({
        clubId,
        body: {
          members: importableRows.map((row) => ({
            name: row.name ?? '',
            email: row.email ?? '',
            roleId: MEMBER_ROLE_ID,
          })),
        },
      }).unwrap()
    } catch (error) {
      console.error('Failed to import members', error)
      setErrorMessage(resolveImportErrorMessage(error))
    }
  }

  const summaryStats: SummaryStat[] = summary
    ? [
        {
          label: 'Total rows',
          value: summary.totalRows,
          className: 'bg-gray-50 text-gray-700',
        },
        {
          label: 'Importable',
          value: summary.importable,
          className: 'bg-green-50 text-green-700',
        },
        {
          label: 'Invalid',
          value: summary.invalid,
          className: 'bg-red-50 text-red-700',
        },
        {
          label: 'Duplicates',
          value: summary.duplicatesInFile,
          className: 'bg-amber-50 text-amber-700',
        },
        {
          label: 'Already members',
          value: summary.alreadyMembers,
          className: 'bg-amber-50 text-amber-700',
        },
      ]
    : []

  return (
    <Dialog open={isOpen} onClose={handleClose} className="relative z-10">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500/75 transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in"
      />
      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className="relative transform overflow-hidden rounded-lg bg-white px-5 py-6 text-left shadow-xl transition-all data-closed:translate-y-4 data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in sm:my-8 sm:w-full sm:max-w-3xl sm:p-6 data-closed:sm:translate-y-0 data-closed:sm:scale-95">
            <div className="flex items-start justify-between">
              <DialogTitle
                as="h3"
                className="text-lg font-semibold text-gray-900">
                Import members
              </DialogTitle>
              <button
                type="button"
                onClick={handleClose}
                className="-m-2 rounded-md p-2 text-gray-400 hover:text-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                <span className="sr-only">Close</span>
                <XMarkIcon aria-hidden="true" className="size-5" />
              </button>
            </div>
            <p className="mt-2 text-sm text-gray-500">
              Upload a Toastmasters roster export (CSV). Only the Name and Email
              columns are used — everything else is ignored.
            </p>

            <div className="mt-6">
              <label
                htmlFor="import-members-file"
                className="block text-sm font-medium text-gray-900">
                CSV file
              </label>
              <input
                ref={fileInputRef}
                id="import-members-file"
                name="file"
                type="file"
                accept=".csv,text/csv"
                onChange={handleFileChange}
                disabled={isLoading}
                className="mt-1 block w-full text-sm text-gray-900 file:mr-4 file:rounded-md file:border-0 file:bg-indigo-600 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60"
              />
              {fileName && (
                <p className="mt-1 text-xs text-gray-400">{fileName}</p>
              )}
            </div>

            {(isLoading || isImporting) && (
              <div className="mt-6 flex justify-center">
                <LoadingSpinner
                  message={isImporting ? 'Importing members...' : 'Reading file...'}
                />
              </div>
            )}

            {errorMessage && !isLoading && !isImporting && (
              <p
                className="mt-6 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700"
                role="alert">
                {errorMessage}
              </p>
            )}

            {batchResult && !isImporting && !errorMessage && (
              <ImportResultSummary result={batchResult} />
            )}

            {summary && !isLoading && !isImporting && !errorMessage && !batchResult && (
              <div className="mt-6">
                <SummaryBar stats={summaryStats} />
              </div>
            )}

            {rows.length > 0 &&
              !isLoading &&
              !isImporting &&
              !errorMessage &&
              !batchResult && (
              <div className="scrollbar-always mt-6 max-h-80 overflow-y-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="sticky top-0 bg-white">
                    <tr>
                      <th
                        scope="col"
                        className="py-3 pr-3 pl-4 text-left text-sm font-semibold text-gray-700 sm:pl-0">
                        Name
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3 text-left text-sm font-semibold text-gray-700">
                        Email
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3 text-left text-sm font-semibold text-gray-700">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {rows.map((row) => (
                      <tr key={row.rowNumber}>
                        <td className="py-3 pr-3 pl-4 text-sm whitespace-nowrap text-gray-700 sm:pl-0">
                          {row.name || (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-3 py-3 text-sm whitespace-nowrap text-gray-500">
                          {row.email || (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-3 py-3 text-sm">
                          <StatusBadge status={row.status} />
                          {row.message && (
                            <p className="mt-1 text-xs text-gray-400">
                              {row.message}
                            </p>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {!batchResult &&
              !isLoading &&
              !isImporting &&
              !errorMessage &&
              importableRows.length > 0 && (
              <p className="mt-6 rounded-md bg-blue-50 px-3 py-2 text-sm text-blue-700">
                Each imported member will be emailed an invite to join the club on speech agenda
              </p>
            )}

            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={handleClose}
                className="inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:w-auto">
                {batchResult ? 'Done' : 'Close'}
              </button>
              {!batchResult && rows.length > 0 && (
                <button
                  type="button"
                  onClick={handleImport}
                  disabled={importableRows.length === 0 || isImporting}
                  className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto">
                  Import {importableRows.length}{' '}
                  {importableRows.length === 1 ? 'member' : 'members'}
                </button>
              )}
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  )
}

export default ImportMembersDialog
